import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import SectionTitle from 'components/ui/typography/SectionTitle';
import { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { MODELS_MAPS, HistoryModelConfig } from '../config/modelsMap';
import { baseCell } from 'components/cells';
import {
  filterOperators,
  singleSelectFilterOperators,
  dateFilterOperators
} from 'components/enhanced';

export const useVersionsColumns = (
  onOpenDialog: (id: string) => void,
  onOpenObjectHistory?: (modelName: string, objectId: string) => void
): GridColDef[] => {
  const { t } = useTranslation();
  const linkStyle: CSSProperties = {
    textDecoration: 'none',
    color: '#1976d2',
    cursor: 'pointer'
  };
  const disabledStyle: CSSProperties = { color: '#888', cursor: 'not-allowed' };

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: t(`global.version_id`),
      filterOperators,
      width: 200,
      renderCell: (params) => {
        const actionRaw = params.row?.action;
        const isDelete = String(actionRaw ?? '')
          .trim()
          .toLowerCase()
          .includes('delete');

        if (isDelete) {
          return (
            <span style={disabledStyle} title="Versione eliminazione">
              <SectionTitle>{params.row.id}</SectionTitle>
            </span>
          );
        }

        return (
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              onOpenDialog(params.row.id);
            }}
            style={linkStyle}>
            <SectionTitle>{params.row.id}</SectionTitle>
          </a>
        );
      }
    },
    {
      field: 'action',
      headerName: t(`global.action`),
      filterOperators,
      width: 200,
      renderCell: baseCell
    },
    {
      field: 'model_name',
      headerName: t(`global.model`) || 'Modello',
      type: 'singleSelect',
      filterOperators: singleSelectFilterOperators,
      valueOptions: (Object.entries(MODELS_MAPS) as [string, HistoryModelConfig][]).map(
        ([value, cfg]) => ({
          value,
          label: cfg.label
        })
      ),
      width: 180,
      valueFormatter: (params) => {
        const code = String(params?.value ?? '').trim();
        return MODELS_MAPS[code]?.label ?? code;
      },
      renderCell: (params: GridRenderCellParams) => {
        const code = String(params.value ?? '').trim();
        if (!code) return baseCell(params);
        return <span>{MODELS_MAPS[code]?.label ?? code}</span>;
      }
    },
    {
      field: 'object_id',
      headerName: 'Object ID',
      filterOperators,
      width: 140,
      renderCell: (params: GridRenderCellParams) => {
        const modelName = params.row?.model_name;
        const objectId = params.value;
        if (!modelName || objectId == null || String(objectId).trim() === '') {
          return baseCell(params);
        }

        const objectIdStr = String(objectId).trim();
        if (onOpenObjectHistory) {
          return (
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                onOpenObjectHistory(String(modelName).trim(), objectIdStr);
              }}
              style={linkStyle}>
              <SectionTitle>{objectIdStr}</SectionTitle>
            </a>
          );
        }

        return (
          <NavLink to={`/tenant/versions/${modelName}/${objectId}`} style={linkStyle}>
            <SectionTitle>{objectId}</SectionTitle>
          </NavLink>
        );
      }
    },
    {
      field: 'object_value',
      headerName: t(`global.value`),
      filterOperators,
      width: 220,
      renderCell: baseCell
    },
    {
      field: 'timestamp',
      headerName: t(`global.date`),
      filterOperators: dateFilterOperators,
      width: 200,
      renderCell: (params: GridRenderCellParams) => {
        const date = new Date(params.value as string);
        return date.toLocaleString('default', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
    },
    {
      field: 'actor',
      headerName: t(`global.user`),
      filterOperators,
      width: 200,
      renderCell: (params) => {
        const value = params.value;
        return value == null ? 'SYSTEM' : baseCell(params);
      }
    }
  ];
  return columns;
};
